import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { guestChannelPreferenceService } from "@/services/guest-channel-preference-service";
import {
  GuestChannelPreference,
  UpsertGuestChannelPreferenceRequest,
} from "@/types/guest-channel-preference";
import { is404 } from "@/utils/httpErrors";

export const guestChannelPreferenceKeys = {
  all: ["guestChannelPreference"] as const,
  detail: (guestId: string, hotelId: string) =>
    [...guestChannelPreferenceKeys.all, "detail", guestId, hotelId] as const,
};

const noPreference = (
  guestId: string,
  hotelId: string,
): GuestChannelPreference => ({
  guestId,
  hotelId,
  preferred: null,
  createdAt: "",
  updatedAt: "",
});

export const useGuestChannelPreference = (
  guestId: string | null | undefined,
  hotelId: string | null | undefined,
) =>
  useQuery<GuestChannelPreference>({
    queryKey: guestChannelPreferenceKeys.detail(guestId ?? "", hotelId ?? ""),
    queryFn: async () => {
      try {
        return await guestChannelPreferenceService.fetch(
          guestId as string,
          hotelId as string,
        );
      } catch (error) {
        if (is404(error)) {
          return noPreference(guestId as string, hotelId as string);
        }
        throw error;
      }
    },
    enabled: !!guestId && !!hotelId,
    staleTime: 5 * 60 * 1000,
  });

interface OptimisticContext {
  previous: GuestChannelPreference | undefined;
}

const invalidateDetail = (
  qc: ReturnType<typeof useQueryClient>,
  guestId: string,
  hotelId: string,
): Promise<void> =>
  qc.invalidateQueries({
    queryKey: guestChannelPreferenceKeys.detail(guestId, hotelId),
  });

export const useUpsertGuestChannelPreference = (
  guestId: string,
  hotelId: string,
) => {
  const qc = useQueryClient();
  return useMutation<
    GuestChannelPreference,
    unknown,
    UpsertGuestChannelPreferenceRequest,
    OptimisticContext
  >({
    mutationFn: (payload) =>
      guestChannelPreferenceService.upsert(guestId, hotelId, payload),
    onMutate: async (payload) => {
      await qc.cancelQueries({
        queryKey: guestChannelPreferenceKeys.detail(guestId, hotelId),
      });
      const previous = qc.getQueryData<GuestChannelPreference>(
        guestChannelPreferenceKeys.detail(guestId, hotelId),
      );
      qc.setQueryData<GuestChannelPreference>(
        guestChannelPreferenceKeys.detail(guestId, hotelId),
        (current) =>
          current
            ? { ...current, preferred: payload.preferred }
            : noPreference(guestId, hotelId),
      );
      return { previous };
    },
    onError: (_err, _payload, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(
          guestChannelPreferenceKeys.detail(guestId, hotelId),
          ctx.previous,
        );
      }
    },
    onSettled: () => invalidateDetail(qc, guestId, hotelId),
  });
};

export const useClearGuestChannelPreference = (
  guestId: string,
  hotelId: string,
) => {
  const qc = useQueryClient();
  return useMutation<void, unknown, void, OptimisticContext>({
    mutationFn: () => guestChannelPreferenceService.clear(guestId, hotelId),
    onMutate: async () => {
      await qc.cancelQueries({
        queryKey: guestChannelPreferenceKeys.detail(guestId, hotelId),
      });
      const previous = qc.getQueryData<GuestChannelPreference>(
        guestChannelPreferenceKeys.detail(guestId, hotelId),
      );
      qc.setQueryData<GuestChannelPreference>(
        guestChannelPreferenceKeys.detail(guestId, hotelId),
        (current) =>
          current
            ? { ...current, preferred: null }
            : noPreference(guestId, hotelId),
      );
      return { previous };
    },
    onError: (_err, _payload, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(
          guestChannelPreferenceKeys.detail(guestId, hotelId),
          ctx.previous,
        );
      }
    },
    onSettled: () => invalidateDetail(qc, guestId, hotelId),
  });
};
